// ─── Intake Form Models ───────────────────────────────────────────────────────

export interface IntakeOnlineData {
  intake_first_name: string;
  intake_last_name: string;
  company_id: number;
  location_id: number;
  intake_phone: string;
  intake_email: string;
  intake_address: string;
  intake_city: string;
  intake_province: string;
  intake_postal: string;
  intake_gender: string;
  intake_dob: string;
  intake_health_card: string;
  terms: boolean;
}

export interface IntakeJsonData {
  // Emergency Contact
  D16?: string; //emergency ContactName
  D17?: string; //emergency ContactRelationship
  D18?: string; //emergency ContactPhone
  D19: string; //emergency Alternate Phone

  // Health Insurance
  D85: string; //Insurance Company Name
  D86: string; //Insurance Policy Number
  D87: string; //Insurance Group Number
  D88: RelationshipType; //Relationship to Insured Party

  // Visit Information
  D28: string; //Reason for Visit
  D29: string[];
  

  // Medical History
  D63: YesNoType; //Diabetic
  D35: string; //Blood Sugar Level
  D62: YesNoNaType; //Pregnant
  D33: YesNoType; //Smoker
  D79: YesNoType; //Are you an existing patient?
  D80: GenderType; //Are you an existing patient?
  hasD24: YesNoType;
  D24: string; //Eye Surgery Details
  hasD36: YesNoType;
  D36: string; //injury
  D30: string; //medications
  D25: string[]; //medical conditions
  D31: string; //allergies
  D26: string[]; //family eye conditions
  D27: string; //family doctor name
  D64: string; //family doctor phone
}

// ─── Union / Enum Types ───────────────────────────────────────────────────────

export type GenderType        = '' | 'Male' | 'Female' | 'Non-binary' | 'Prefer not to say' | 'Other';
export type RelationshipType  = '' | 'Self' | 'Spouse / Partner' | 'Child' | 'Other';
export type YesNoType         = '' | 'Yes' | 'No';
export type YesNoNaType       = '' | 'Yes' | 'No' | 'N/A';
export type CorrectionType    = '' | 'None' | 'Glasses' | 'Contact Lenses' | 'Both';
export type DiabeticType      = '' | 'Type 1' | 'Type 2' | 'Gestational' | 'Pre-diabetic';

// ─── Error Map ────────────────────────────────────────────────────────────────

export type IntakeFormErrors = Partial<Record<keyof IntakeOnlineData, boolean>>;
export type IntakeJsonErrors = Partial<Record<keyof IntakeJsonData, boolean>>;

// ─── Constants ────────────────────────────────────────────────────────────────

export const GENDER_OPTIONS: GenderType[] = [
  'Male', 'Female', 'Non-binary', 'Prefer not to say', 'Other',
];

export const CANADIAN_PROVINCES: string[] = [
  'AB', 'BC', 'MB', 'NB', 'NL',
  'NS', 'NT', 'NU', 'ON', 'PE',
  'QC', 'SK', 'YT',
];

export const RELATIONSHIP_OPTIONS: RelationshipType[] = [
  'Self', 'Spouse / Partner', 'Child', 'Other',
];

export const CORRECTION_TYPE_OPTIONS: CorrectionType[] = [
  'None', 'Glasses', 'Contact Lenses', 'Both',
];

export const DIABETIC_TYPE_OPTIONS: DiabeticType[] = [
  'Type 1', 'Type 2', 'Gestational', 'Pre-diabetic',
];

export const LENS_WEAR_DURATION_OPTIONS: string[] = [
  'Less than 1 year', '1–3 years', '3–5 years', '5–10 years', '10+ years',
];

export const MEDICAL_CONDITIONS: string[] = [
  'Hypertension (High Blood Pressure)',
  'Heart Disease',
  'Thyroid Disease',
  'Arthritis',
  'Lupus / Autoimmune Disease',
  'HIV / AIDS',
  'Cancer',
  'Neurological Disorder',
  'Kidney Disease',
  'None of the above',
];

export const FAMILY_EYE_CONDITIONS: string[] = [
  'Glaucoma',
  'Macular Degeneration',
  'Cataracts',
  'Retinal Disease',
  'Diabetic Retinopathy',
  'Keratoconus',
  'Strabismus (Crossed Eyes)',
  'Amblyopia (Lazy Eye)',
  'None of the above',
];

export const VISION_SYMPTOMS: string[] = [
  'Blurry Vision',
  'Double Vision',
  'Eye Pain or Discomfort',
  'Frequent Headaches',
  'Dry or Itchy Eyes',
  'Excessive Tearing',
  'Sensitivity to Light',
  'Floaters or Flashes',
  'Difficulty Seeing at Night',
  'Redness or Swelling',
  'Burning or Stinging',
  'None',
];

// ─── Initial State ────────────────────────────────────────────────────────────

export const INITIAL_FORM_STATE: IntakeOnlineData = {
  intake_first_name: '',
  intake_last_name: '',
  company_id: 0,
  location_id: 0,
  intake_phone: '',
  intake_email: '',
  intake_address: '',
  intake_city: '',
  intake_province: '',
  intake_postal: '',
  intake_gender: '',
  intake_dob: '',
  intake_health_card: '',
  terms: false
};

export const INITIAL_JSON_STATE: IntakeJsonData = {
  D16: '',
  D17: '',
  D18: '',
  D19: '',
  D85: '',
  D86: '',
  D87: '',
  D88: '',
  D28: '',
  D29: [],
  D63: '',
  D35: '',
  D62: '',
  D33: '',
  D79: '',
  D80: '',
  hasD24: '',
  D24: '',
  hasD36: '',
  D36: '',
  D30: '',
  D31: '',
  D25: [],
  D26: [],
  D27: '',
  D64: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const toggleArrayItem = (arr: string[], item: string): string[] =>
  arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];

// ─── Validation ───────────────────────────────────────────────────────────────

export const validateForm = (form: IntakeOnlineData, jsonData: IntakeJsonData): [IntakeFormErrors, IntakeJsonErrors] => {
  const errors: IntakeFormErrors = {};
  const errjson: IntakeJsonErrors = {};

  if (!form.intake_first_name.trim()) errors.intake_first_name = true;
  if (!form.intake_last_name.trim()) errors.intake_last_name = true;
  if (!form.intake_phone.trim()) errors.intake_phone = true;
  if (!form.intake_dob) errors.intake_dob = true;
  if (!jsonData.D79) errjson.D79 = true;
  if (!form.terms) errors.terms = true;

  return [errors, errjson];
};